// @ts-check
import {
  createUserPreference
} from './user-preference'
import { consentOrPayPreferences } from './consentOrPayPreferences'
import pick from 'lodash.pick'
import {
  consentOrPayDisplayPurposes,
  nonConsentOrPayDisplayPurposes
} from './display-purposes'

/**
 * @param {IntMap<Vendor>} vendors
 * @param {(...any) => void} log
 * @returns {IntMap<Vendor>}
 */
function removeUnconsentableVendors(vendors, log) {
  const consentableVendors = Object.values(vendors).filter((vendor) => {
    const consentable =
      vendor.purposes.length !== 0 ||
      vendor.legIntPurposes.length !== 0 ||
      vendor.specialPurposes.length !== 0
    !consentable && log('removing', vendor.id, vendor.name)
    return consentable
  })
  return consentableVendors.reduce((acc, vendor) => {
    acc[vendor.id] = vendor
    return acc
  }, {})
}

/**
 * @param {IntMap<Vendor>} vendors
 * @param {Array<number>} publisherRestrictions
 * @returns {IntMap<Vendor>}
 */
function applyFlexiblePurposes(vendors, publisherRestrictions) {
  const restrictPurposeIds = new Set([...publisherRestrictions])
  return Object.entries(vendors).reduce((acc, [id, v]) => {
    v.purposes = Array.from(
      new Set([
        ...v.purposes,
        ...v.flexiblePurposes.filter((purposeId) =>
          restrictPurposeIds.has(purposeId)
        )
      ])
    )
    v.legIntPurposes = v.legIntPurposes.filter(
      (purposeId) => !restrictPurposeIds.has(purposeId)
    )
    acc[id] = v
    return acc
  }, {})
}

/**
 * @param {Array<import('./display-purposes').DisplayPurposes>} displayPurposes
 * @returns {Array<number>}
 */
const getStackIds = (displayPurposes) =>
  displayPurposes
    .filter((purpose) => purpose.type === 'stacks')
    .map(({ id }) => id)

/**
 * @param {UIModelOpts} opts
 * @returns {UIModel}
 */
export function createModel({
  cmpModel,
  currentLevel,
  log,
  uiLevel,
  consentOrPay,
  isMailEssentialSubscriber,
  consentOrPaySubscriptionURL,
  isUserLoggedIn,
  isNewUser,
  allowRejectAll,
  tcfCacheKey
}) {
  const gvl = cmpModel.tcModel.gvl
  const {
    purposes,
    specialPurposes,
    features,
    specialFeatures,
    dataCategories
  } = gvl
  const stacks = consentOrPay
    ? pick(gvl.stacks, getStackIds(consentOrPayDisplayPurposes))
    : pick(gvl.stacks, getStackIds(nonConsentOrPayDisplayPurposes))

  const displayPurposes = consentOrPay
    ? consentOrPayDisplayPurposes
    : nonConsentOrPayDisplayPurposes
  const vendors = applyFlexiblePurposes(
    removeUnconsentableVendors(JSON.parse(JSON.stringify(gvl.vendors)), log),
    cmpModel.tcModel.publisherRestrictions
      .getRestrictions()
      .map((restriction) => restriction.purposeId)
  )

  const userPrefs =
    consentOrPay && !isMailEssentialSubscriber
      ? consentOrPayPreferences(cmpModel, isNewUser)
      : createUserPreference(cmpModel)
  const nonTcfVendors = cmpModel.acmVendors.cvl
  const nonRegisteredVendors = cmpModel.nonRegisteredVendors.cvl
  return {
    config: {
      hash: {
        purposes,
        specialPurposes,
        features,
        specialFeatures,
        vendors,
        stacks,
        nonTcfVendors,
        nonRegisteredVendors,
        dataCategories
      },
      userPrefs,
      displayPurposes,
      consentOrPay,
      consentOrPaySubscriptionURL,
      isMailEssentialSubscriber,
      isUserLoggedIn,
      allowRejectAll,
      tcfCacheKey
    },
    currentLevel: uiLevel || currentLevel
  }
}

/**
 * @typedef {Object} UIModelOpts
 * @property {import('./Ui').UiLevel} currentLevel
 * @property {import('./Ui').UiLevel} uiLevel
 * @property {import('./Ui').CmpModelForUi} cmpModel
 * @property {boolean} consentOrPay
 * @property {boolean} isMailEssentialSubscriber
 * @property {string} [consentOrPaySubscriptionURL]
 * @property {boolean} isUserLoggedIn
 * @property {boolean} isNewUser
 * @property {boolean} allowRejectAll
 * @property {string} [tcfCacheKey]
 * @property {(...any) => void} log
 */

/**
 * @typedef {Object} UIModel
 * @property {UIModelConfig} config
 * @property {import('./Ui').UiLevel} currentLevel
 */

/**
 * @typedef {Object} UIModelConfig
 * @property {UIGVLModel} hash
 * @property {import('./user-preference').UserPrefs} userPrefs
 * @property {DisplayPurposes} displayPurposes
 * @property {boolean} consentOrPay
 * @property {string|undefined} consentOrPaySubscriptionURL
 * @property {boolean} isMailEssentialSubscriber
 * @property {boolean} isUserLoggedIn
 * @property {boolean} allowRejectAll
 * @property {string} [tcfCacheKey]
 */
/**
 * @typedef {Object} UIGVLModel
 * @property {IntMap<Purpose>} purposes
 * @property {IntMap<Purpose>} specialPurposes
 * @property {IntMap<Feature>} features
 * @property {IntMap<Purpose>} specialFeatures
 * @property {IntMap<Vendor>} vendors
 * @property {IntMap<Stack>} stacks
 * @property {IntMap<DataCategory> } [dataCategories]
 * @property {import('../cmp/NonIABModel').NonIABVendorList} nonTcfVendors
 * @property {import('../cmp/NonIABModel').NonIABVendorList} nonRegisteredVendors
 */

/**
 * @template T
 * @typedef {import('@iabtechlabtcf/core').IntMap<T>} IntMap
 */

/**
 * @typedef {import('@iabtechlabtcf/core').Purpose} Purpose
 */

/**
 * @typedef {import('@iabtechlabtcf/core').Feature} Feature
 */

/**
 * @typedef {import('@iabtechlabtcf/core').Vendor} Vendor
 */

/**
 * @typedef {import('@iabtechlabtcf/core').Stack} Stack
 */

/**
 * @typedef {Array<{type: string, id: number, isConsentImmutable?: boolean, isLIImmutable?: boolean}>} DisplayPurposes
 */

/**
 * @typedef {import('@iabtechlabtcf/core').DataCategory} DataCategory
 */
